import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Search,
  ChevronRight,
  ArrowUpRight,
  Building2,
  MapPin,
  Users,
  Factory,
} from "lucide-react";
import { fetcher } from "@/lib/api";
import { PersonAvatar } from "@/components/PersonAvatar";
import { SemaforoDot } from "@/components/Semaforo";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";

function buildTree(companies) {
  const divs = [];
  const dmap = {};
  companies.forEach((c) => {
    if (!dmap[c.division]) {
      dmap[c.division] = { name: c.division, id: c.division_id, zmap: {}, zonas: [] };
      divs.push(dmap[c.division]);
    }
    const d = dmap[c.division];
    if (!d.zmap[c.zona]) {
      d.zmap[c.zona] = { name: c.zona, id: c.zona_id, rmap: {}, regionales: [] };
      d.zonas.push(d.zmap[c.zona]);
    }
    const z = d.zmap[c.zona];
    if (!z.rmap[c.regional]) {
      z.rmap[c.regional] = { name: c.regional, id: c.regional_id, empresas: [] };
      z.regionales.push(z.rmap[c.regional]);
    }
    z.rmap[c.regional].empresas.push(c);
  });
  return divs;
}

function EmpresaItem({ emp, onPick }) {
  return (
    <button
      onClick={() => onPick("empresa", emp.id)}
      data-testid={`tree-empresa-${emp.id}`}
      className="w-full group flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-[#E2333F]/5 transition-colors text-left"
    >
      <SemaforoDot status={emp.status} size={8} />
      <PersonAvatar name={emp.gerente} size="xs" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-slate-800 truncate group-hover:text-[#E2333F]">
            {emp.name}
          </span>
          {emp.is_real && (
            <span className="text-[8px] font-bold uppercase bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded-sm">
              real
            </span>
          )}
        </div>
        <span className="text-[11px] text-slate-400 truncate block">{emp.gerente || "Sin asignar"}</span>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-[#E2333F]" />
    </button>
  );
}

function Collapsible({ label, icon: Icon, children, level = 0, count, onNavigate, defaultOpen = false }) {
  const [open, setOpen] = React.useState(defaultOpen);
  const pad = { 0: "pl-0", 1: "pl-3", 2: "pl-6" }[level] || "pl-0";
  return (
    <div className={pad}>
      <div className="w-full flex items-center gap-1 rounded-sm hover:bg-slate-100 transition-colors">
        <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 px-2 py-1.5 flex-1 text-left min-w-0">
          <ChevronRight className={`w-3.5 h-3.5 text-slate-400 transition-transform shrink-0 ${open ? "rotate-90" : ""}`} />
          <Icon className="w-4 h-4 text-[#E2333F] shrink-0" />
          <span className={`flex-1 truncate ${level === 0 ? "font-display font-bold text-slate-900 text-sm" : "font-semibold text-slate-700 text-sm"}`}>
            {label}
          </span>
          {typeof count === "number" && <span className="text-[11px] text-slate-400 font-medium">{count}</span>}
        </button>
        {onNavigate && (
          <button
            onClick={onNavigate}
            title="Abrir dashboard"
            data-testid={`tree-open-${level}`}
            className="px-2 py-1.5 text-slate-300 hover:text-[#E2333F] transition-colors shrink-0"
          >
            <ArrowUpRight className="w-4 h-4" />
          </button>
        )}
      </div>
      {open && <div className="border-l border-slate-100 ml-3">{children}</div>}
    </div>
  );
}

export function StructureTree({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [query, setQuery] = React.useState("");
  const { data: companies } = useQuery({
    queryKey: ["companies"],
    queryFn: () => fetcher("/companies"),
  });

  const pick = (level, id) => {
    onOpenChange(false);
    if (level === "empresa") navigate(`/company/${encodeURIComponent(id)}`);
    else navigate(`/${level}/${encodeURIComponent(id)}`);
  };

  const tree = React.useMemo(() => buildTree(companies || []), [companies]);

  const filtered = React.useMemo(() => {
    if (!query.trim()) return null;
    const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const q = norm(query);
    return (companies || []).filter(
      (c) =>
        norm(c.name).includes(q) ||
        norm(c.gerente).includes(q) ||
        norm(c.regional).includes(q) ||
        norm(c.zona).includes(q),
    );
  }, [companies, query]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-[400px] sm:w-[440px] p-0 flex flex-col" data-testid="structure-tree-sheet">
        <SheetHeader className="p-5 pb-3 border-b border-slate-200">
          <SheetTitle className="font-display font-black text-xl flex items-center gap-2">
            <span className="bg-[#E2333F] text-white text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm">
              Estructura
            </span>
            Grupo Tomza
          </SheetTitle>
          <SheetDescription>
            Atajo a cualquier empresa. Busca o navega el árbol organizacional.
          </SheetDescription>
          <div className="relative mt-2">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="structure-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar empresa, gerente o zona…"
              className="w-full h-10 pl-9 pr-3 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#E2333F] focus:border-[#E2333F]"
            />
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-3">
          {filtered ? (
            <div className="space-y-0.5">
              {filtered.length === 0 && (
                <p className="text-sm text-slate-400 px-2 py-4">Sin resultados para “{query}”.</p>
              )}
              {filtered.map((emp) => (
                <div key={emp.id}>
                  <EmpresaItem emp={emp} onPick={pick} />
                  <div className="text-[10px] text-slate-400 pl-10 -mt-1 mb-1 truncate">
                    {emp.division} · {emp.zona} · {emp.regional}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-1">
              {tree.map((div) => (
                <Collapsible
                  key={div.name}
                  label={div.name}
                  icon={Building2}
                  level={0}
                  onNavigate={(e) => { e.stopPropagation(); pick("division", div.id); }}
                  count={div.zonas.reduce(
                    (a, z) => a + z.regionales.reduce((b, r) => b + r.empresas.length, 0),
                    0,
                  )}
                >
                  {div.zonas.map((zona) => (
                    <Collapsible
                      key={zona.name}
                      label={zona.name}
                      icon={MapPin}
                      level={1}
                      onNavigate={(e) => { e.stopPropagation(); pick("zona", zona.id); }}
                    >
                      {zona.regionales.map((reg) => (
                        <Collapsible
                          key={reg.name}
                          label={reg.name}
                          icon={Users}
                          level={2}
                          count={reg.empresas.length}
                          onNavigate={(e) => { e.stopPropagation(); pick("regional", reg.id); }}
                        >
                          <div className="pl-5 space-y-0.5 py-1">
                            {reg.empresas.map((emp) => (
                              <EmpresaItem key={emp.id} emp={emp} onPick={pick} />
                            ))}
                          </div>
                        </Collapsible>
                      ))}
                    </Collapsible>
                  ))}
                </Collapsible>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
